import { NAME_FIRST_YEAR } from "@listou/shared/schemas/prenoms";
import { Link } from "@tanstack/react-router";
import { useId, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";

import { DEPARTMENT_PATHS } from "./departments";
import { GIRLS, GRID, number, percent } from "./format";

const WIDTH = 900;
const PAD = { left: 52, right: 16, top: 16, bottom: 30 };
const PLOT_W = WIDTH - PAD.left - PAD.right;

export type Series = { label: string; color: string; values: (number | null)[] };

/**
 * Yearly line or area chart. The pointer, the arrow keys and the optional year slider all
 * drive one hover index; the tooltip is drawn in the SVG so nothing reads `window` on render.
 */
export function TimeChart({
  series,
  label,
  height = 300,
  area = false,
  reverse = false,
  format = number,
  unit,
  marker,
}: {
  series: Series[];
  label: string;
  height?: number;
  area?: boolean;
  /** Rank charts: 1 at the top. */
  reverse?: boolean;
  format?: (value: number) => string;
  unit?: string;
  /** An annotated year, such as the peak. */
  marker?: { year: number; text: string };
}) {
  const [hover, setHover] = useState<number | null>(null);
  const gradient = useId();
  const plotH = height - PAD.top - PAD.bottom;
  const count = series[0]?.values.length ?? 0;
  const all = series.flatMap((s) => s.values).filter((v): v is number => v !== null && v > 0);
  const top = Math.max(1, ...all);
  const bottom = reverse ? Math.min(1, ...all) : 0;
  const x = (index: number) => PAD.left + (index / Math.max(1, count - 1)) * PLOT_W;
  const y = (value: number) => {
    const ratio = reverse ? (value - bottom) / Math.max(1, top - bottom) : value / top;
    return PAD.top + (reverse ? ratio : 1 - ratio) * plotH;
  };
  const ticks = reverse
    ? [bottom, Math.round((top + bottom) / 2), top]
    : [0, 0.5, 1].map((r) => Math.round(top * r));
  const segments = (values: (number | null)[]) => {
    const parts: [number, number][][] = [];
    let current: [number, number][] = [];
    for (const [index, value] of values.entries()) {
      if (value === null || (reverse && value === 0)) {
        if (current.length > 0) parts.push(current);
        current = [];
      } else current.push([x(index), y(value)]);
    }
    if (current.length > 0) parts.push(current);
    return parts;
  };
  const move = (event: PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const ratio = ((event.clientX - box.left) / box.width) * WIDTH;
    setHover(
      Math.min(count - 1, Math.max(0, Math.round(((ratio - PAD.left) / PLOT_W) * (count - 1)))),
    );
  };
  const key = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key === "ArrowLeft") setHover((hover ?? count) - 1 < 0 ? 0 : (hover ?? count) - 1);
    else if (event.key === "ArrowRight") setHover(Math.min(count - 1, (hover ?? -1) + 1));
    else if (event.key === "Escape") setHover(null);
    else return;
    event.preventDefault();
  };
  const hoverX = hover === null ? 0 : x(hover);
  const lines = hover === null ? [] : series.map((s) => ({ s, value: s.values[hover] ?? null }));
  const tipW = 168;
  const tipH = 26 + lines.length * 20;
  const tipX = hoverX > WIDTH / 2 ? hoverX - tipW - 12 : hoverX + 12;
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      role="img"
      aria-label={label}
      tabIndex={0}
      className="w-full touch-pan-y select-none rounded-lg"
      onPointerMove={move}
      onPointerDown={move}
      onPointerLeave={() => setHover(null)}
      onKeyDown={key}
      onBlur={() => setHover(null)}
    >
      <defs>
        {series.map((s, index) => (
          <linearGradient key={s.label} id={`${gradient}-${index}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
          </linearGradient>
        ))}
      </defs>
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(tick)} y2={y(tick)} stroke={GRID} />
          <text x={PAD.left - 8} y={y(tick) + 4} textAnchor="end" fontSize="12" fill="#6b7090">
            {reverse ? `${tick}e` : format(tick)}
          </text>
        </g>
      ))}
      {[1900, 1920, 1940, 1960, 1980, 2000, 2020].map((year) => (
        <text
          key={year}
          x={x(year - NAME_FIRST_YEAR)}
          y={height - 8}
          textAnchor="middle"
          fontSize="12"
          fill="#6b7090"
        >
          {year}
        </text>
      ))}
      {series.map((s, index) =>
        segments(s.values).map((points, part) => {
          const path = points.map(([px, py]) => `${px},${py}`).join(" ");
          const first = points[0];
          const last = points.at(-1);
          return (
            <g key={`${s.label}-${part}`}>
              {area && !reverse && first && last && (
                <polygon
                  points={`${first[0]},${y(0)} ${path} ${last[0]},${y(0)}`}
                  fill={`url(#${gradient}-${index})`}
                />
              )}
              <polyline
                points={path}
                fill="none"
                stroke={s.color}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {points.length === 1 && first && (
                <circle cx={first[0]} cy={first[1]} r="3.5" fill={s.color} />
              )}
            </g>
          );
        }),
      )}
      {marker && (
        <g>
          <line
            x1={x(marker.year - NAME_FIRST_YEAR)}
            x2={x(marker.year - NAME_FIRST_YEAR)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="#1f2544"
            strokeOpacity="0.25"
            strokeDasharray="2 5"
          />
          <text
            x={x(marker.year - NAME_FIRST_YEAR) + (marker.year > 1990 ? -6 : 6)}
            y={PAD.top + 12}
            textAnchor={marker.year > 1990 ? "end" : "start"}
            fontSize="13"
            fontWeight="700"
            fill="#1f2544"
            stroke="#fff"
            strokeWidth="4"
            paintOrder="stroke"
          >
            {marker.text}
          </text>
        </g>
      )}
      {hover !== null && (
        <g pointerEvents="none">
          <line
            x1={hoverX}
            x2={hoverX}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="#1f2544"
            strokeOpacity="0.5"
          />
          {lines.map(({ s, value }) =>
            value === null || (reverse && value === 0) ? null : (
              <circle
                key={s.label}
                cx={hoverX}
                cy={y(value)}
                r="5"
                fill={s.color}
                stroke="#fff"
                strokeWidth="2"
              />
            ),
          )}
          <g transform={`translate(${tipX} ${PAD.top + 4})`}>
            <rect width={tipW} height={tipH} rx="10" fill="#1f2544" />
            <text x="12" y="19" fontSize="13" fontWeight="700" fill="#fff">
              {NAME_FIRST_YEAR + hover}
            </text>
            {lines.map(({ s, value }, index) => (
              <g key={s.label} transform={`translate(12 ${38 + index * 20})`}>
                <circle r="4" cy="-4" fill={s.color} stroke="#fff" strokeWidth="1" />
                <text x="12" fontSize="13" fill="#fff">
                  {value === null || (reverse && value === 0)
                    ? `${s.label} : non classé`
                    : `${s.label} : ${reverse ? `${value}e` : format(value)}${unit ? ` ${unit}` : ""}`}
                </text>
              </g>
            ))}
          </g>
        </g>
      )}
    </svg>
  );
}

/** A name's whole life in one small line: enough to tell a fad from a classic at a glance. */
export function MiniChart({ values, color }: { values: number[]; color: string }) {
  const top = Math.max(1, ...values);
  const points = values.map(
    (value, index) => `${(index / (values.length - 1)) * 120},${34 - (value / top) * 32}`,
  );
  return (
    <svg viewBox="0 0 120 36" className="h-9 w-full" aria-hidden="true" preserveAspectRatio="none">
      <polygon points={`0,36 ${points.join(" ")} 120,36`} fill={color} fillOpacity="0.15" />
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Girls and boys as a share of the births with a name, year by year. */
export function ShareChart({
  girls,
  boys,
  label,
}: {
  girls: number[];
  boys: number[];
  label: string;
}) {
  const shares = girls.map((g, index) => {
    const total = g + (boys[index] ?? 0);
    return total === 0 ? null : (g / total) * 100;
  });
  return (
    <TimeChart
      series={[{ label: "Part de filles", color: GIRLS, values: shares }]}
      label={label}
      height={220}
      area
      format={(value) => percent(value)}
    />
  );
}

export function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-2">
          <span className="inline-block h-1 w-5 rounded-full" style={{ background: item.color }} />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/**
 * The metropolitan map. `fill` colors a department by code, `href` makes it a link,
 * `describe` is what the tooltip and screen readers read. Overseas departments live in lists.
 */
export function FranceMap({
  label,
  fill,
  describe,
  href,
}: {
  label: string;
  fill: (code: string) => string;
  describe: (code: string) => string;
  href?: (code: string) => { to: string };
}) {
  const [active, setActive] = useState<string | null>(null);
  return (
    <div>
      <svg viewBox="0 0 390 370" className="mx-auto w-full max-w-md" role="img" aria-label={label}>
        <title>{label}</title>
        {DEPARTMENT_PATHS.map((department) => {
          const path = (
            <path
              d={department.path}
              fill={fill(department.code)}
              stroke="#fff"
              strokeWidth="0.8"
              className={href ? "cursor-pointer" : undefined}
              onMouseEnter={() => setActive(department.code)}
              onFocus={() => setActive(department.code)}
              onMouseLeave={() => setActive(null)}
            >
              <title>{describe(department.code)}</title>
            </path>
          );
          const target = href?.(department.code);
          return target ? (
            <Link key={department.code} to={target.to} aria-label={describe(department.code)}>
              {path}
            </Link>
          ) : (
            <g key={department.code} tabIndex={0} role="img" aria-label={describe(department.code)}>
              {path}
            </g>
          );
        })}
      </svg>
      <p className="mt-3 min-h-6 text-center text-sm" aria-live="polite">
        {active ? describe(active) : "Survolez un département pour lire sa valeur."}
      </p>
    </div>
  );
}

/** A ranking row: position, name, bar scaled to the best row, and a trailing slot for movement. */
export function BarRow({
  rank,
  name,
  value,
  ratio,
  color,
  children,
  suffix,
}: {
  rank?: number;
  name: ReactNode;
  value: string;
  /** Bar length, 0 to 1, relative to the best row. */
  ratio: number;
  color: string;
  children?: ReactNode;
  suffix?: ReactNode;
}) {
  return (
    <li className="grid grid-cols-[2rem_1fr_auto] items-center gap-x-3 gap-y-1 py-1.5">
      <span className="text-right text-sm text-muted tabular-nums">{rank ?? ""}</span>
      <span className="min-w-0 truncate font-bold">{name}</span>
      <span className="text-sm tabular-nums">
        {value}
        {suffix}
      </span>
      <span />
      <span className="h-1.5 rounded-full bg-line">
        <span
          className="block h-full rounded-full"
          style={{ width: `${Math.max(2, Math.min(100, ratio * 100))}%`, background: color }}
        />
      </span>
      <span className="text-xs text-muted">{children}</span>
    </li>
  );
}
